//@ts-nocheck
import React, { useEffect, useState } from "react";
import useField from "../../hooks/useField";
import { useSingleton, useTheme } from "../../hooks/useSingleton";
import { useAnalytics } from "../../hooks/useAnalytics";
import { UserBehaviorAction } from "../../types/analyticsStandards";
import { ExpandedContext } from "../../utils/ExpandedContext";
import FieldMenu from "../FieldMenu/FieldMenu";
import CheckboxField from "../FieldTypes/CheckboxField/CheckboxField";
import DateField from "../FieldTypes/DateField/DateField";
import DropdownField from "../FieldTypes/DropdownField/DropdownField";
import ImageField from "../FieldTypes/ImageField/ImageField";
import ImageRollField from "../FieldTypes/ImageRollField/ImageRollField";
import NoteField from "../FieldTypes/NoteField/NoteField";
import Number from "../FieldTypes/Number/Number";
import PhoneField from "../FieldTypes/PhoneField/PhoneField";
import RadioField from "../FieldTypes/RadioField/RadioField";
import ReadOnlyText from "../FieldTypes/ReadOnlyText/ReadOnlyText";
import TableField from "../FieldTypes/TableField/TableField";
import TextField from "components/FieldTypes/TextField/TextField";
import GeoSpatialField from "components/FieldTypes/GeoField/GeoSpatialField";
import styles from "./FieldAction.module.scss";
import { FieldInstance } from "db/definitions";
import enforceCharacterLimitAction from "../../utils/actions/enforceCharacterLimitAction";
import Section from "components/FieldTypes/Section/Section";
import { PhotoLog } from "components/FieldTypes/PhotoLog/PhotoLog";
import { EmbeddedTextInput } from "components/FieldTypes/GeoField/EmbeddedTextField/EmbeddedTextField";
import GeoSpatialFieldUTM from "components/FieldTypes/GeoField/GeoSpatialFieldUTM";
import { ConfirmationModal } from "@factorearth/component-library";

/**
 * The field action component to handle specific field recording actions
 * @param {Object} props for the component
 * @param {FieldInstance} fieldInstance the field template data combined with the instance.
 * @param {FieldTemplate} fieldTemplate for the field
 * @param {Object} updateObj the data recieved for this field from the real time subscription
 * @param {Boolean} fromTable toggle determining if the field is within a table or not
 * @param {Function} onSubmitCb callback to call after submit
 * @param {String} formGroupType detailing the type of form group.
 * @param {ID} projectId of the current project
 * @param {Boolean} online network status for current user
 * @param {User} userDoc of the current user
 * @param {Function} triggerUpdateDataForOffline callback to handle alt instances offline.
 */
function FieldAction(props) {
	const {
		setupDrawer,
		fieldInstance,
		fieldInstanceId,
		fieldTemplate,
		updateObj,
		altUpdateObj,
		fromTable,
		setupChanged,
		onSubmitCb,
		formGroupType,
		projectId,
		online,
		userDoc,
		triggerUpdateDataForOffline,
		rowData,
	} = props;

	const [loading, setLoading] = useState(!!fieldTemplate.options?.nextToken);
	const [fieldHtml, setFieldHtml] = useState(null);
	const [openFieldMenu, setOpenFieldMenu] = useState(false);
	const [fieldId, setFieldId] = useState(null);
	const { mode } = useSingleton();
	const analytics = useAnalytics({
		userId: userDoc?.id || "",
		groupId: userDoc?.group || "",
		online: online || true,
	});
	const [editOpen, setEditOpen] = useState(true);
	const color = useTheme();

	const {
		activeEditing,
		currentFieldInstance,
		editing,
		handleChange,
		handleFocus,
		handleRefreshField,
		handleClickAndSubmit,
		handleSubmit,
		handleUpdate,
		saveError,
		clearSaveError,
	} = useField(onSubmit, fieldInstance, online, userDoc, setupChanged);
	// One toast per failed save; the text stays on screen and is resubmitted automatically (see utils/connectionStatus).
	const [saveErrorToastOpen, setSaveErrorToastOpen] = useState(false);
	useEffect(() => {
		if (saveError) setSaveErrorToastOpen(true);
	}, [saveError]);
	useEffect(() => {
		if (!saveErrorToastOpen && saveError) clearSaveError();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [saveErrorToastOpen]);

	const handleExportSettings = () => {
		setEditOpen(true);
	};

	function toggleLoading(newBool) {
		setLoading(newBool);
	}

	async function removeUser() {
		await handleRefreshField();
	}

	function handleFocusField() {
		if (openFieldMenu) {
			handleHistory(fieldTemplate);
		}

		handleFocus();
	}

	async function handleChangeHandler(e, submitCb) {
		if (openFieldMenu) {
			handleHistory(fieldTemplate);
		}
		handleChange(e, submitCb);
	}

	/**
	 * Callback called after submission of the field.
	 * @param data from useField for the field instance.
	 */
	async function onSubmit(data: { updatedField: FieldInstance }) {
		if (online) {
			if (onSubmitCb) {
				onSubmitCb(data);
			}
		} else {
			if (fieldInstance.altInstanceId) {
				triggerUpdateDataForOffline?.(data);
			}
		}
	}

	/**
	 * Reverts field value to data.
	 * @param {Object} data to revert to
	 */
	function handleRevert(data) {
		const obj = { target: { value: data } };
		if (openFieldMenu) {
			handleHistory(fieldTemplate);
		}

		analytics
			.trackUserBehaviorEvent(UserBehaviorAction.HISTORY_REVERT, {
				fieldId: fieldTemplate?.id,
				userId: userDoc?.id,
			})
			.catch((error) => {
				console.error("Failed to track history revert event:", error);
			});

		handleClickAndSubmit(obj);
	}

	/**
	 * History handler..
	 */
	function handleHistory(temp) {
		setOpenFieldMenu(!openFieldMenu);
		setFieldId(temp.id);
	}

	// This use effect is to manage the received updateObj changes that come from the cloud update event
	useEffect(() => {
		if (new Date(updateObj?.updatedAt) > new Date(currentFieldInstance.updatedAt)) {
			if (!editing) {
				handleUpdate(updateObj);
			} else if (editing && !activeEditing && fieldTemplate.action) {
				handleUpdate(updateObj);
			}
		}
	}, [updateObj, editing, currentFieldInstance.updatedAt]);

	useEffect(() => {
		if (altUpdateObj) {
			if (!editing || (editing && !activeEditing && fieldTemplate.action)) {
				handleLinkFromUpdate(altUpdateObj);
			}
		}
	}, [altUpdateObj, editing]);

	/**
	 * Handles updates for fields with the "LINK_FROM" action.
	 * Checks if the incoming data's value matches the trigger value specified in the action data.
	 * If matched, updates the current field instance's value with the set value from action data, updatedAt, and authors accordingly.
	 * @param {Object} data - The update object containing the new value, updatedAt, and authors.
	 */
	async function handleLinkFromUpdate(data) {
		const index = fieldTemplate.action.indexOf("LINK_FROM");
		if (index === -1) return;

		// Need to figure out the new value and if its the trigger from the actionData
		const actionDataParts = fieldTemplate.actionData[index].split("_");
		const triggerValue = actionDataParts[1];

		// If no trigger value is configured (empty or legacy "undefined" string), directly mirror the LINK_TO field's value.
		// Use handleSubmit (not handleClickAndSubmit) so it doesn't set editing/activeEditing to true,
		// which would block the useEffect condition from firing on subsequent updates.
		if (!triggerValue || triggerValue === "undefined") {
			const updatedFieldInstance = { ...currentFieldInstance };
			updatedFieldInstance.value = data.value;
			updatedFieldInstance.updatedAt = data.updatedAt;
			updatedFieldInstance.authors = data.authors;
			handleUpdate(updatedFieldInstance);
			await handleSubmit(data.value);
			return;
		}

		if (data.value !== triggerValue) return;

		const newValue = actionDataParts[2];
		// Just need to pull over the value, updatedAt, authors
		const updatedFieldInstance = { ...currentFieldInstance };
		updatedFieldInstance.value = newValue;
		updatedFieldInstance.updatedAt = data.updatedAt;
		updatedFieldInstance.authors = data.authors;
		const syntheticEvent = { target: { value: updatedFieldInstance.value } };
		await handleClickAndSubmit(syntheticEvent);
		handleUpdate(updatedFieldInstance);
	}

	async function renderFieldWithPaginatedOptions(template) {
		const newFieldTemplate = { ...template };
		if (template.options) {
			const retrievedOptions = await Promise.resolve(template.options.items);
			newFieldTemplate.options.items = retrievedOptions;
		}
		toggleLoading(false);
		const html = handleTypes(newFieldTemplate);
		setFieldHtml(html);
	}

	// In order to get all the updates from updateObj to the children, we need it in this dependency array.
	useEffect(() => {
		renderFieldWithPaginatedOptions(fieldTemplate);
	}, [currentFieldInstance, fieldId, mode, openFieldMenu, updateObj]);

	/**
	 * Method to handle setting up specific components based on the field's template type.
	 * @returns html
	 */
	function handleTypes(optionalTemplate) {
		const template = optionalTemplate || fieldTemplate;
		// Actions being handled here
		if (template.action.includes("HIDDEN")) {
			return null;
		}

		const charLimit = enforceCharacterLimitAction(template.action, template.actionData);

		switch (template.inputType) {
			case "text":
				return (
					<div data-test="field-text">
						<TextField
							username={currentFieldInstance.editing}
							authUsername={userDoc.username}
							color={color}
							updateObj={updateObj}
							fieldInstance={currentFieldInstance}
							fieldTemplate={template}
							handleHistory={handleHistory}
							fieldInstanceId={fieldInstanceId}
							value={currentFieldInstance.value}
							handleChange={handleChangeHandler}
							handleFocus={handleFocusField}
							onBlur={handleSubmit}
							fromTable={fromTable}
							activeEditing={activeEditing}
							user={userDoc}
							moreHorizon={
								<div data-test="field-text-menu">
									<FieldMenu
										handleUpdate={handleUpdate}
										username={currentFieldInstance.editing}
										authUsername={userDoc.username}
										handleRevert={handleRevert}
										online={online}
										removeUser={removeUser}
										openFieldMenu={openFieldMenu}
										fieldTemplate={template}
										userDoc={userDoc}
										currentFieldInstance={currentFieldInstance}
									/>
								</div>
							}
						/>
					</div>
				);
			case "note":
				return (
					<div data-test="field-note">
						<ExpandedContext.Consumer>
							{({ expandedField }) => (
								<NoteField
									username={currentFieldInstance.editing}
									authUsername={userDoc.username}
									color={color}
									updateObj={updateObj}
									fieldInstance={currentFieldInstance}
									fieldTemplate={template}
									value={currentFieldInstance.value}
									handleChange={handleChangeHandler}
									handleChangeAndSubmit={handleClickAndSubmit}
									handleHistory={handleHistory}
									onBlur={handleSubmit}
									fromTable={fromTable}
									user={userDoc}
									expandedField={expandedField}
									// activeEditing={activeEditing}
									charLimit={charLimit}
									moreHorizon={
										<div data-test="field-note-menu">
											<FieldMenu
												handleUpdate={handleUpdate}
												username={currentFieldInstance.editing}
												authUsername={userDoc.username}
												handleRevert={handleRevert}
												online={online}
												removeUser={removeUser}
												openFieldMenu={openFieldMenu}
												fieldTemplate={template}
												userDoc={userDoc}
												currentFieldInstance={currentFieldInstance}
											/>
										</div>
									}
								/>
							)}
						</ExpandedContext.Consumer>
					</div>
				);
			case "readonlytext":
				return (
					<div className={fromTable ? styles.tableRow : styles.wrapperRow} data-test="field-read-only-text">
						<ReadOnlyText color={color} fieldInstanceId={fieldInstanceId} fieldTemplate={fieldTemplate} fromTable={fromTable} />
					</div>
				);
			case "section":
				return (
					<div className={styles.wrapperRow} data-test="field-section">
						<Section color={color} fieldInstanceId={fieldInstanceId} fieldTemplate={fieldTemplate} fromTable={fromTable} />
					</div>
				);
			case "phone":
				return (
					<div className={fromTable ? styles.tableRow : styles.wrapperRow} data-test="field-phone">
						<PhoneField
							username={currentFieldInstance.editing}
							authUsername={userDoc.username}
							updateObj={updateObj}
							fieldInstance={currentFieldInstance}
							fieldTemplate={template}
							handleHistory={handleHistory}
							fieldInstanceId={fieldInstanceId}
							value={currentFieldInstance.value}
							handleChange={handleChangeHandler}
							handleFocus={handleFocusField}
							onBlur={handleSubmit}
							fromTable={fromTable}
							activeEditing={activeEditing}
							user={userDoc}
						/>
						<div className={fromTable ? styles.table_history_wrapper : styles.history_wrapper} data-test="field-phone-menu">
							<FieldMenu
								handleUpdate={handleUpdate}
								username={currentFieldInstance.editing}
								authUsername={userDoc.username}
								handleRevert={handleRevert}
								online={online}
								fromTable={fromTable}
								removeUser={removeUser}
								fieldTemplate={template}
								userDoc={userDoc}
								openFieldMenu={openFieldMenu}
								currentFieldInstance={currentFieldInstance}
							/>
						</div>
					</div>
				);
			case "date":
				return (
					<div data-test="field-date">
						<DateField
							username={currentFieldInstance.editing}
							authUsername={userDoc.username}
							color={color}
							handleHistory={handleHistory}
							updateObj={updateObj}
							fieldInstance={currentFieldInstance}
							fieldTemplate={template}
							value={currentFieldInstance.value}
							handleChange={handleChange}
							handleClickAndSubmit={handleClickAndSubmit}
							onBlur={handleSubmit}
							fromTable={fromTable}
							user={userDoc}
							moreHorizon={
								<div data-test="field-date-menu">
									<FieldMenu
										handleUpdate={handleUpdate}
										username={currentFieldInstance.editing}
										authUsername={userDoc.username}
										handleRevert={handleRevert}
										online={online}
										removeUser={removeUser}
										openFieldMenu={openFieldMenu}
										fieldTemplate={template}
										userDoc={userDoc}
										currentFieldInstance={currentFieldInstance}
									/>
								</div>
							}
						/>
					</div>
				);
			case "number":
				return (
					<div data-test="field-number">
						<Number
							authUsername={userDoc.username}
							color={color}
							fieldInstance={currentFieldInstance}
							fieldTemplate={template}
							fromTable={fromTable}
							handleChange={handleChange}
							handleClickAndSubmit={handleClickAndSubmit}
							handleHistory={handleHistory}
							onBlur={handleSubmit}
							username={currentFieldInstance.editing}
							value={currentFieldInstance.value}
							charLimit={charLimit}
							moreHorizon={
								<div data-test="field-number-menu">
									<FieldMenu
										handleUpdate={handleUpdate}
										username={currentFieldInstance.editing}
										authUsername={userDoc.username}
										handleRevert={handleRevert}
										online={online}
										removeUser={removeUser}
										openFieldMenu={openFieldMenu}
										fieldTemplate={template}
										userDoc={userDoc}
										currentFieldInstance={currentFieldInstance}
									/>
								</div>
							}
						/>
					</div>
				);
			case "checkbox":
				return (
					<div data-test="field-checkbox">
						<CheckboxField
							fromTable={fromTable}
							color={color}
							updateObj={updateObj}
							fieldInstance={currentFieldInstance}
							fieldTemplate={template}
							value={currentFieldInstance.value}
							handleChange={handleClickAndSubmit}
							handleHistory={handleHistory}
							handleSubmit={handleSubmit}
							user={userDoc}
							moreHorizon={
								<div data-test="field-checkbox-menu">
									<FieldMenu
										handleUpdate={handleUpdate}
										username={currentFieldInstance.editing}
										authUsername={userDoc.username}
										handleRevert={handleRevert}
										online={online}
										fromTable={fromTable}
										removeUser={removeUser}
										fieldTemplate={template}
										userDoc={userDoc}
										currentFieldInstance={currentFieldInstance}
									/>
								</div>
							}
						/>
					</div>
				);
			case "radio":
				return (
					<div data-test="field-radio">
						<RadioField
							color={color}
							updateObj={updateObj}
							fieldInstance={currentFieldInstance}
							fieldTemplate={template}
							value={currentFieldInstance.value}
							handleChange={handleClickAndSubmit}
							handleHistory={handleHistory}
							handleSubmit={handleSubmit}
							user={userDoc}
							fromTable={fromTable}
							moreHorizon={
								<div data-test="field-radio-menu">
									<FieldMenu
										handleUpdate={handleUpdate}
										username={currentFieldInstance.editing}
										authUsername={userDoc.username}
										handleRevert={handleRevert}
										online={online}
										removeUser={removeUser}
										userDoc={userDoc}
										fieldTemplate={fieldTemplate}
										currentFieldInstance={currentFieldInstance}
										fromTable={fromTable}
									/>
								</div>
							}
						/>
					</div>
				);
			case "table":
				return (
					<div data-test="field-table">
						<TableField
							setupDrawer={setupDrawer}
							color={color}
							updateObj={updateObj}
							initialFieldInstance={fieldInstance}
							fieldTemplate={template}
							value={currentFieldInstance.value}
							handleChange={handleChangeHandler}
							handleSubmit={handleSubmit}
							online={online}
							userDoc={userDoc}
							projectId={projectId}
						/>
					</div>
				);
			case "dropdown":
				return (
					<div className={styles.wrapper} data-test="field-dropdown">
						<DropdownField
							color={color}
							updateObj={updateObj}
							fromTable={fromTable}
							fieldInstance={currentFieldInstance}
							fieldTemplate={template}
							value={currentFieldInstance.value}
							handleChange={handleClickAndSubmit}
							handleHistory={handleHistory}
							handleSubmit={handleSubmit}
							online={online}
							user={userDoc}
							moreHorizon={
								<div data-test="field-dropdown-menu">
									<FieldMenu
										handleUpdate={handleUpdate}
										username={currentFieldInstance.editing}
										authUsername={userDoc.username}
										handleRevert={handleRevert}
										online={online}
										removeUser={removeUser}
										userDoc={userDoc}
										fieldTemplate={fieldTemplate}
										currentFieldInstance={currentFieldInstance}
										fromTable={fromTable}
									/>
								</div>
							}
						/>
					</div>
				);
			case "image":
				return (
					<div data-test="field-image">
						<ImageField
							userDoc={userDoc}
							fieldInstance={currentFieldInstance}
							handleChangeCb={handleChangeHandler}
							online={online}
							setupChanged={setupChanged}
							fromTable={fromTable}
							rowData={rowData}
						/>
					</div>
				);
			case "imageroll":
				return (
					<div data-test="field-image-roll">
						<PhotoLog
							setupDrawer={setupDrawer}
							formGroupType={formGroupType}
							projectId={projectId}
							updateObj={updateObj}
							userDoc={userDoc}
							fieldInstance={fieldInstance}
							fieldTemplate={template}
							online={online}
							user={userDoc}
							handleExportSettings={handleExportSettings}
						/>
					</div>
				);
			case "geospatial":
				return (
					<div data-test="field-geospatial">
						<GeoSpatialField
							username={currentFieldInstance.editing}
							authUsername={userDoc.username}
							color={color}
							updateObj={updateObj}
							fieldInstance={currentFieldInstance}
							fieldTemplate={template}
							value={currentFieldInstance.value}
							handleChange={handleChangeHandler}
							handleChangeAndSubmit={handleClickAndSubmit}
							handleHistory={handleHistory}
							onBlur={handleSubmit}
							fromTable={fromTable}
							userDoc={userDoc}
							// activeEditing={activeEditing}
							charLimit={charLimit}
							moreHorizon={
								<div data-test="field-note-menu">
									<FieldMenu
										handleUpdate={handleUpdate}
										username={currentFieldInstance.editing}
										authUsername={userDoc.username}
										handleRevert={handleRevert}
										online={online}
										removeUser={removeUser}
										openFieldMenu={openFieldMenu}
										fieldTemplate={template}
										userDoc={userDoc}
										currentFieldInstance={currentFieldInstance}
									/>
								</div>
							}
						/>
					</div>
				);
			case "geospatialutm":
				return (
					<div data-test="field-geospatialutm">
						<GeoSpatialFieldUTM
							username={currentFieldInstance.editing}
							authUsername={userDoc.username}
							color={color}
							updateObj={updateObj}
							fieldInstance={currentFieldInstance}
							fieldTemplate={template}
							value={currentFieldInstance.value}
							handleChange={handleChangeHandler}
							handleChangeAndSubmit={handleClickAndSubmit}
							handleHistory={handleHistory}
							onBlur={handleSubmit}
							fromTable={fromTable}
							userDoc={userDoc}
							// activeEditing={activeEditing}
							charLimit={charLimit}
							moreHorizon={
								<div data-test="field-note-menu">
									<FieldMenu
										handleUpdate={handleUpdate}
										username={currentFieldInstance.editing}
										authUsername={userDoc.username}
										handleRevert={handleRevert}
										online={online}
										removeUser={removeUser}
										openFieldMenu={openFieldMenu}
										fieldTemplate={template}
										userDoc={userDoc}
										currentFieldInstance={currentFieldInstance}
									/>
								</div>
							}
						/>
					</div>
				);
			// case "embeddedtext":
			// 	return (
			// 		<div data-test="field-embedded-text">
			// 			<EmbeddedTextInput
			// 				fieldInstance={currentFieldInstance}
			// 				fieldTemplate={template}
			// 				colorPalette={color}
			// 				value={currentFieldInstance.value}
			// 				onChange={handleChangeHandler}
			// 				handleBlur={handleSubmit}
			// 				// widthCh={charLimit}
			// 			/>
			// 		</div>
			// 	);

			default:
				return null;
		}
	}

	return (
		<React.Fragment key={`FieldActionField-${fieldInstanceId}`}>
			{saveError && (
				<ConfirmationModal
					colorPalette={color}
					details={`"${saveError}" could not be saved. Check your connection. Your text is kept on screen and the app will keep trying; do not reload until the folder shows Saved.`}
					confirmTitle="Not saved"
					open={saveErrorToastOpen}
					setOpen={setSaveErrorToastOpen}
					toast="destructive"
					timeout={8000}
					canClose
				/>
			)}
			<div id="fieldRow" data-test="field-row">
				{!loading ? fieldHtml : <div>LOADING</div>}
			</div>
		</React.Fragment>
	);
}

export default FieldAction;
